import { createAction, createAsyncAction, createReducer, isActionOf } from 'typesafe-actions'
import { combineReducers } from 'redux'
import { RequestStatus } from '../../responses/response'
import { ID } from '../../types/UtilityTypes'
import { toggleReducer } from '../../utils/redux-utils'
import { Tag } from '../../responses/tags'
import { projectActions } from '../data/project/project-actions'
import { combineEpics } from 'redux-observable'
import { RootEpic } from '../root'
import { filter, map, mergeMap } from 'rxjs/operators'
import { apiRequests } from '../../http/config'
import { setProp } from '../../utils/utils'
import { pipe } from 'fp-ts/function'
import { taskActions } from '../data/task/task-actions'
import { EMPTY_TASK } from '../../responses/tasks'
import * as R from 'fp-ts/Record'

interface SubscriptionData {
    projectId: ID,
    userId: ID,
}

const actions = {
    startFileUpload: createAction('@PROJECT_DETAIL/START_FILE_UPLOAD')<File[]>(),
    cancelFileUpload: createAction('@PROJECT_DETAIL/CANCEL_FILE_UPLOAD')(),
    toggleObject: createAction('@PROJECT_DETAIL/TOGGLE_OBJECT')<ID>(),
    openObject: createAction('@PROJECT_DETAIL/OPEN_OBJECT')<ID>(),
    openTask: createAction('@PROJECT_DETAIL/OPEN_TASK')<{ parentId: ID, taskId: ID }>(),
    subscribe: createAsyncAction(
        '@PROJECT_DETAIL/SUBSCRIBE_REQUEST',
        '@PROJECT_DETAIL/SUBSCRIBE_SUCCESS',
        '@PROJECT_DETAIL/SUBSCRIBE_FAILURE',
    )<SubscriptionData, SubscriptionData, void>(),
    unsubscribe: createAsyncAction(
        '@PROJECT_DETAIL/UNSUBSCRIBE_REQUEST',
        '@PROJECT_DETAIL/UNSUBSCRIBE_SUCCESS',
        '@PROJECT_DETAIL/UNSUBSCRIBE_FAILURE',
    )<SubscriptionData, SubscriptionData, void>(),
}

export { actions as projectDetailActions }

export interface ProjectDetailState {
    state: RequestStatus,
    tags: Record<ID, Tag[]>,
    openObjects: Record<ID, boolean>,
    openTasks: Record<ID, ID>,
}

export const openTaskReducer = createReducer<Record<ID, ID>>({})
    .handleAction(taskActions.startAppend, (state, action) => pipe(state, R.mapWithIndex((parent, task) => task || parent), R.insertAt(action.payload.parentId as string, EMPTY_TASK.id)))
    .handleAction(taskActions.cancelEdit, (state) => pipe(state, R.filter(task => task !== 0)))
    .handleAction(taskActions.remove.success, (state, action) => pipe(state, R.filter(task => task !== action.payload)))
    .handleAction(taskActions.create.success, (state, action) =>
        action.meta === 'user' && action.payload.parent_id ?
            pipe(state, setProp(action.payload.parent_id, action.payload.id)) :
            state
    )

export const projectDetailReducer = combineReducers<ProjectDetailState>({
    state: createReducer<RequestStatus>(RequestStatus.INITIAL)
        .handleAction(projectActions.get.request, () => RequestStatus.FETCHING)
        .handleAction(projectActions.get.success, () => RequestStatus.SUCCESS)
        .handleAction(projectActions.get.failure, () => RequestStatus.FAILURE),
    tags: createReducer<Record<ID, Tag[]>>({}),
    openObjects: createReducer<Record<ID, boolean>>({})
        .handleAction(actions.toggleObject, toggleReducer)
        .handleAction(actions.openObject, (state, action) => ({...state, [action.payload]: true})),
    openTasks: openTaskReducer
        .handleAction(actions.openTask, (state, action) => pipe(state, setProp(action.payload.parentId, action.payload.taskId))),
})

const subscribe: RootEpic = action$ => action$.pipe(
    filter(isActionOf(actions.subscribe.request)),
    mergeMap(action => apiRequests.project.subscribe({
        data: {
            users: [action.payload.userId],
        },
        params: {
            projectId: action.payload.projectId,
        }
    }).pipe(
        map(res => res.response.success ?
            actions.subscribe.success(action.payload) :
            actions.subscribe.failure()
        ),
    )),
)

const unsubscribe: RootEpic = action$ => action$.pipe(
    filter(isActionOf(actions.unsubscribe.request)),
    mergeMap(action => apiRequests.project.unsubscribe({
        data: {
            users: [action.payload.userId],
        },
        params: {
            projectId: action.payload.projectId,
        }
    }).pipe(
        map(res => res.response.success ?
            actions.unsubscribe.success(action.payload) :
            actions.unsubscribe.failure()
        ),
    )),
)

export const projectDetailEpic = combineEpics(subscribe, unsubscribe)